import type { RagOperationKind, RagOperationMetric, RagOperationMetricUnit, SearchScope } from "@gencow/core";
import { sql, type SQL } from "drizzle-orm";

export type RagMetricsDbLike = {
  execute(query: SQL): Promise<unknown>;
};

export type RagMetricInput = {
  appId?: string;
  corpus: string;
  visibility: SearchScope["visibility"];
  ownerUserId?: string | null;
  operation: RagOperationKind;
  jobId?: string | null;
  workflowId?: string | null;
  sourceId?: string | null;
  metricName: string;
  metricValue: number;
  unit?: RagOperationMetricUnit | null;
  metadata?: Record<string, unknown>;
};

type RagMetricRow = {
  id: string;
  app_id: string;
  corpus: string;
  visibility_scope: SearchScope["visibility"];
  owner_user_id: string | null;
  operation: RagOperationKind;
  job_id: string | null;
  workflow_id: string | null;
  source_id: string | null;
  metric_name: string;
  metric_value: number | string;
  unit: RagOperationMetricUnit | null;
  metadata: unknown;
  recorded_at: string | Date;
};

export function resolveRagMetricAppId(appId?: string): string {
  return appId?.trim() || process.env.GENCOW_APP_NAME?.trim() || "app";
}

export function rowsFromMetricResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function emitRagOperationMetric(db: RagMetricsDbLike, input: RagMetricInput): Promise<void> {
  if (!Number.isFinite(input.metricValue)) return;

  await db.execute(sql`
    INSERT INTO rag_operation_metrics (
      id,
      app_id,
      corpus,
      visibility_scope,
      owner_user_id,
      operation,
      job_id,
      workflow_id,
      source_id,
      metric_name,
      metric_value,
      unit,
      metadata
    )
    VALUES (
      ${crypto.randomUUID()},
      ${resolveRagMetricAppId(input.appId)},
      ${input.corpus},
      ${input.visibility},
      ${input.ownerUserId ?? null},
      ${input.operation},
      ${input.jobId ?? null},
      ${input.workflowId ?? null},
      ${input.sourceId ?? null},
      ${input.metricName},
      ${input.metricValue},
      ${input.unit ?? null},
      ${JSON.stringify(input.metadata ?? {})}::jsonb
    )
  `);
}

export async function emitRagOperationMetrics(db: RagMetricsDbLike, inputs: RagMetricInput[]): Promise<void> {
  for (const input of inputs) {
    await emitRagOperationMetric(db, input);
  }
}

export function mapRagMetricRow(row: RagMetricRow): RagOperationMetric {
  return {
    id: row.id,
    appId: row.app_id,
    corpus: row.corpus,
    visibility: row.visibility_scope,
    ownerUserId: row.owner_user_id,
    operation: row.operation,
    jobId: row.job_id,
    workflowId: row.workflow_id,
    sourceId: row.source_id,
    metricName: row.metric_name,
    metricValue: Number(row.metric_value),
    unit: row.unit,
    metadata:
      row.metadata && typeof row.metadata === "object" && !Array.isArray(row.metadata)
        ? (row.metadata as Record<string, unknown>)
        : {},
    recordedAt: row.recorded_at instanceof Date ? row.recorded_at.toISOString() : String(row.recorded_at),
  };
}
